import type { DialogCloseListener } from './dialogCloseSlotContext'
import type { JsResultProperties } from './types'

export function shouldTriggerStage3DialogClose(
  props: Pick<JsResultProperties, 'close__after__complete' | 'return__from__dialog'>,
  actionIsContextXml: string,
): boolean {
  const isContextCall = actionIsContextXml === 'call'
  const result = props.close__after__complete || (props.return__from__dialog && isContextCall)
  return result
}

export function resolveListenersFromProperties(
  props: Pick<JsResultProperties, 'close__after__complete' | 'return__from__dialog'>,
  actionIsContextXml: string,
): DialogCloseListener[] {
  const listeners: DialogCloseListener[] = []
  if (props.close__after__complete) {
    listeners.push('dialogClosed')
  }
  const isContextCall = actionIsContextXml === 'call'
  // Swing closeRoots(DO_RETURN): onDialogClose(child UniData) → dialogClosed, then dispose → NReturn.
  if (props.return__from__dialog && isContextCall) {
    if (!listeners.includes('dialogClosed')) {
      listeners.push('dialogClosed')
    }
    listeners.push('dialogClosedNReturn')
  }
  return listeners
}

/**
 * Swing ActionCall.closeRoots after NEW_WINDOW / NEW_DIALOG (isNewThread):
 * DO_RETURN / CLOSE_AFTER_COMPLETE close the action form window even when
 * XML isContext is call&explicit / call&doc (stage3 must not abort stage4).
 */
export function shouldTriggerCloseRootsAfterNewThread(
  props: Pick<JsResultProperties, 'close__after__complete' | 'return__from__dialog'>,
  windowType?: string | null,
): boolean {
  const wt = String(windowType ?? '')
    .trim()
    .toUpperCase()
  if (wt !== 'NEW_WINDOW' && wt !== 'NEW_DIALOG') return false
  return Boolean(props.close__after__complete || props.return__from__dialog)
}

/** Listeners for closeRoots — no isContext===call gate (unlike stage3). */
export function resolveCloseRootsListenersFromProperties(
  props: Pick<JsResultProperties, 'close__after__complete' | 'return__from__dialog'>,
): DialogCloseListener[] {
  const listeners: DialogCloseListener[] = []
  if (props.close__after__complete) {
    listeners.push('dialogClosed')
  }
  if (props.return__from__dialog) {
    if (!listeners.includes('dialogClosed')) {
      listeners.push('dialogClosed')
    }
    listeners.push('dialogClosedNReturn')
  }
  return listeners
}

/**
 * After chainCalled en.setContext(), stage 4 is skipped — but XML call&return /
 * transferType *return* still must close the dialog (2577 add_btn → 2280).
 */
export function shouldCloseDialogDespiteSetContextAbort(
  actionIsContextXml: string,
  transferType?: string | null,
): boolean {
  if (String(actionIsContextXml ?? '').trim() === 'call&return') {
    return true
  }
  const type = String(transferType ?? '').trim().toLowerCase()
  if (!type) return false
  const stepNavigationReturnTypes = new Set([
    '5',
    '6',
    '7',
    '8',
    '9',
    '10',
    '11',
    'return_none',
    'return_replace',
    'return_prev_none',
    'return_prev_replace',
    'return_prev_cur_data_none',
    'return_prev_cur_data_replace',
    'return_to_last_form',
  ])
  if (stepNavigationReturnTypes.has(type)) return false
  if (type === '105') return true
  return type.includes('return')
}
